import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import {
  Lock, Search, SlidersHorizontal, X, CheckCircle2,
  Clock, Calendar, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, Users, UserCheck,
  FolderOpen, FileText, Tag, Image as ImageIcon,
} from 'lucide-react';
import { useTaskStore } from '../stores/taskStore';
import { Button, Loader } from '../components/common';
import { Header } from '../components/layout/Header';
import { ImageLightbox } from '../components/tasks/ImageLightbox';
import { attachmentApi } from '../services/api';
import { useRelativeTime } from '../hooks/useRelativeTime';
import { useDateFnsLocale } from '../hooks/useDateFnsLocale';
import { useTranslation } from 'react-i18next';
import type { Task, Attachment } from '../types';

// Helper to format date for input (YYYY-MM-DD)
const toInputDate = (date: Date): string => format(date, 'yyyy-MM-dd');

// Avatar component matching TaskCard style
const UserAvatar: React.FC<{
  user?: { firstName?: string; lastName?: string; avatar?: string } | null;
  variant?: 'default' | 'green';
}> = ({ user, variant = 'default' }) => {
  if (!user) return null;

  if (user.avatar) {
    return (
      <img
        src={user.avatar}
        alt={`${user.firstName} ${user.lastName}`}
        className="w-5 h-5 rounded-full object-cover flex-shrink-0"
      />
    );
  }

  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  const bgClass = variant === 'green'
    ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300';

  return (
    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${bgClass}`}>
      {initials || '?'}
    </div>
  );
};

// Relative time display component - uses i18n language automatically
const RelativeTime: React.FC<{ date: string | undefined | null }> = ({ date }) => {
  const { relativeTime } = useRelativeTime(date);
  const dateFnsLocale = useDateFnsLocale();
  if (!date || !relativeTime) return null;
  return (
    <span className="text-gray-400 dark:text-gray-500" title={format(new Date(date), 'PPP p', { locale: dateFnsLocale })}>
      {relativeTime}
    </span>
  );
};

// Individual history task card with expandable details
const HistoryTaskCard: React.FC<{ task: Task; priority: { bg: string; text: string; border: string } }> = ({ task, priority }) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  const { expandedTaskId, toggleExpand } = useTaskStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isExpanded = expandedTaskId === task.id;

  // Attachments — same pattern as TaskCard
  const attachments = task.attachments || [];
  const attachmentCount = task._count?.attachments ?? attachments.length;

  const [cardAttachments, setCardAttachments] = useState<Attachment[]>(attachments);
  useEffect(() => {
    setCardAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    if (!isExpanded) return;
    if (attachments.length > 0) return;
    if (!task.id || (task._count?.attachments ?? 0) === 0) return;

    let cancelled = false;
    attachmentApi.list(task.id).then((response) => {
      if (!cancelled && response.success && response.data) {
        setCardAttachments(response.data as Attachment[]);
      }
    }).catch(() => {
      // silently ignore
    });

    return () => { cancelled = true; };
  }, [isExpanded, attachments.length, task.id, task._count?.attachments]);

  const displayAttachments = cardAttachments.length > 0 ? cardAttachments : attachments;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Measure expanded content height for smooth animation (same as TaskCard)
  useEffect(() => {
    if (!contentRef.current || !isExpanded) {
      setContentHeight(0);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.target.scrollHeight);
      }
    });

    observer.observe(contentRef.current);
    setContentHeight(contentRef.current.scrollHeight);

    return () => observer.disconnect();
  }, [isExpanded]);

  const handleCardClick = () => {
    toggleExpand(task.id);
  };

  return (
    <div
      className={`
        group rounded-lg border border-l-4 transition-all duration-200 cursor-pointer overflow-hidden
        ${priority.border}
        ${isExpanded
          ? 'shadow-lg border-primary-300 dark:border-primary-700 bg-white dark:bg-gray-900'
          : 'bg-white dark:bg-gray-800/80 border-gray-100 dark:border-gray-700/60 sm:hover:border-gray-200 sm:dark:hover:border-gray-600 sm:hover:shadow-sm'
        }
      `}
    >
      {/* Clickable header area */}
      <div
        className="p-3 sm:p-4"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${task.title}, ${isExpanded ? t('history.expanded') : t('history.collapsed')}`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
      >
        <div className="flex items-start gap-3">
          {/* Completion Status Indicator */}
          <div className="flex-shrink-0 mt-0.5">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Title row */}
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 line-through decoration-gray-300 dark:decoration-gray-600 truncate flex-1 min-w-0">
                {task.title}
              </h3>
              {task.isPrivate && (
                <Lock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              )}
              {/* Expand/collapse indicator - same style as TaskCard */}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-primary-500 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors flex-shrink-0" />
              )}
            </div>

            {/* Description preview */}
            {task.description && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 line-clamp-1">
                {task.description}
              </p>
            )}

            {/* Compact meta row (collapsed view) */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400">
              {/* Project */}
              {task.project && (
                <span className="inline-flex items-center gap-1">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: task.project.color }}
                  />
                  <span className="truncate max-w-[120px]">{task.project.name}</span>
                </span>
              )}

              {/* Priority Badge */}
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${priority.bg} ${priority.text}`}>
                {task.priority}
              </span>

              {/* Attachments count */}
              {(task._count?.attachments ?? (task.attachments?.length ?? 0)) > 0 && (
                <span className="text-gray-400 flex-shrink-0">
                  📎 {task._count?.attachments ?? task.attachments!.length}
                </span>
              )}

              {/* Due date */}
              {task.dueDate && (
                <span className="inline-flex items-center gap-1 text-orange-500 dark:text-orange-400">
                  <Calendar className="w-3 h-3" />
                  <span>{format(new Date(task.dueDate), 'MMM d', { locale: dateFnsLocale })}</span>
                </span>
              )}

              {/* Completed time - relative */}
              {task.completedAt && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3 text-green-500" />
                  <RelativeTime date={task.completedAt} />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expandable content section with smooth animation - same pattern as TaskCard */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isExpanded ? contentHeight : 0 }}
      >
        <div
          ref={contentRef}
          className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-800"
        >
          {/* Full task details grid - same layout as TaskCard expanded section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Description */}
            {task.description && (
              <div className="sm:col-span-2">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="font-medium">{t('history.description')}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 rounded p-2 whitespace-pre-wrap">
                  {task.description}
                </p>
              </div>
            )}

            {/* Priority */}
            <div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <span className="font-medium">{t('history.priority')}</span>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${priority.bg} ${priority.text}`}>
                {task.priority}
              </span>
            </div>

            {/* Project */}
            <div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <FolderOpen className="w-3.5 h-3.5" />
                <span className="font-medium">{t('history.project')}</span>
              </div>
              {task.project ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: `${task.project.color}20`, color: task.project.color }}
                >
                  {task.project.name}
                </span>
              ) : (
                <span className="text-xs text-gray-400">{t('history.noProject')}</span>
              )}
            </div>

            {/* Creator - same style as TaskCard */}
            <div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <Users className="w-3.5 h-3.5" />
                <span className="font-medium">{t('history.creator')}</span>
              </div>
              {task.creator ? (
                <div className="flex items-center gap-2">
                  <UserAvatar user={task.creator} />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {task.creator.firstName} {task.creator.lastName}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-gray-400">-</span>
              )}
            </div>

            {/* Assignee - same style as TaskCard */}
            <div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <UserCheck className="w-3.5 h-3.5" />
                <span className="font-medium">{t('history.assignee')}</span>
              </div>
              {task.assignee ? (
                <div className="flex items-center gap-2">
                  <UserAvatar user={task.assignee} />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {task.assignee.firstName} {task.assignee.lastName}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-gray-400">{t('history.unassigned')}</span>
              )}
            </div>

            {/* Completed By - same style as TaskCard */}
            {task.completedBy && (
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                  <span className="font-medium">{t('history.completedBy')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserAvatar user={task.completedBy} variant="green" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {task.completedBy.firstName} {task.completedBy.lastName}
                  </span>
                </div>
              </div>
            )}

            {/* Completed At */}
            {task.completedAt && (
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Calendar className="w-3.5 h-3.5 text-green-500" />
                  <span className="font-medium">{t('history.completedAt')}</span>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {format(new Date(task.completedAt), 'MMM d, yyyy, HH:mm', { locale: dateFnsLocale })}
                </span>
              </div>
            )}

            {/* Due Date */}
            {task.dueDate && (
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="font-medium">{t('history.dueDate')}</span>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {format(new Date(task.dueDate), 'EEEE, MMMM d, yyyy', { locale: dateFnsLocale })}
                </span>
              </div>
            )}
          </div>

          {/* Labels */}
          {task.labels.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <Tag className="w-3.5 h-3.5" />
                <span className="font-medium">{t('history.labels')}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {task.labels.map((label) => (
                  <span
                    key={label}
                    className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Attachments gallery — expanded view (same as TaskCard) */}
          {displayAttachments.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                <ImageIcon className="w-3.5 h-3.5" />
                <span className="font-medium">{t('history.images')}</span>
                {attachmentCount > displayAttachments.length && (
                  <span className="text-xs text-gray-400">({attachmentCount} {t('history.total')})</span>
                )}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {displayAttachments.map((attachment, index) => (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openLightbox(index); }}
                    className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 text-left"
                    aria-label={t('history.openImageNamed', { index: index + 1, fileName: attachment.fileName })}
                    title={attachment.fileName}
                  >
                    <img
                      src={attachment.url}
                      alt={attachment.fileName}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <span className="absolute bottom-0 left-0 right-0 px-1.5 py-1 text-[10px] text-white bg-black/60 truncate">
                      {attachment.fileName}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Creation timestamp */}
          <div className="mt-4 pt-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {t('history.created')} {format(new Date(task.createdAt), 'MMM d, yyyy, HH:mm', { locale: dateFnsLocale })}
          </div>
        </div>
      </div>

      <ImageLightbox
        images={displayAttachments.map((a) => a.url)}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export const HistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  const { fetchTasksHistory, historyTasks, isLoading } = useTaskStore();

  // Default to last 30 days (including today)
  const today = new Date();
  const defaultStartDate = toInputDate(subDays(today, 30));
  const defaultEndDate = toInputDate(today);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // FIX: Debounced search for real-time filtering on keystroke
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load history with specific filters
  const loadHistoryWithFilters = async (filters: { start?: string; end?: string; search?: string; pageNum?: number }) => {
    const params: Record<string, string | number> = {
      page: filters.pageNum ?? page,
      limit: 20,
    };

    if (filters.start) params.startDate = filters.start;
    if (filters.end) params.endDate = filters.end;
    if (filters.search?.trim()) params.search = filters.search.trim();

    const result = await fetchTasksHistory(params);
    if (result?.meta) {
      setTotalPages(result.meta.totalPages || 1);
      setTotal(result.meta.total || 0);
      setHasSearched(true);
    }
  };

  // Load on mount with default filters
  useEffect(() => {
    loadHistoryWithFilters({ start: startDate, end: endDate, pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Load when page changes (for pagination)
  useEffect(() => {
    if (hasSearched && page > 1) {
      loadHistoryWithFilters({ start: startDate, end: endDate, search: searchQuery, pageNum: page });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleApplyFilters = () => {
    setPage(1);
    loadHistoryWithFilters({
      start: startDate,
      end: endDate,
      search: searchQuery,
      pageNum: 1,
    });
  };

  const handleClearFilters = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setSearchQuery('');
    setPage(1);
    setHasSearched(false);
    setFiltersOpen(false);

    loadHistoryWithFilters({ start: defaultStartDate, end: defaultEndDate, pageNum: 1 });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    handleApplyFilters();
  };

  // Tasks from store (historyTasks)
  const displayedTasks = historyTasks;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400', border: 'border-l-red-500 dark:border-l-red-400' };
      case 'HIGH': return { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-400', border: 'border-l-orange-500 dark:border-l-orange-400' };
      case 'MEDIUM': return { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-l-blue-500 dark:border-l-blue-400' };
      case 'LOW': return { bg: 'bg-gray-50 dark:bg-gray-800/50', text: 'text-gray-600 dark:text-gray-400', border: 'border-l-gray-400 dark:border-l-gray-500' };
      default: return { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', border: 'border-l-gray-400 dark:border-l-gray-500' };
    }
  };

  // Group tasks by completion date
  const groupedTasks = useMemo(() => {
    const groups: Record<string, Task[]> = {};
    displayedTasks.forEach(task => {
      const dateKey = task.completedAt
        ? format(new Date(task.completedAt), 'yyyy-MM-dd', { locale: dateFnsLocale })
        : 'unknown';
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(task);
    });

    // Sort groups by date descending
    const sortedKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    return sortedKeys.map(key => ({
      dateKey: key,
      label: key === 'unknown'
        ? t('dashboard.ui.unknownDate')
        : format(new Date(key + 'T00:00:00'), 'EEEE, MMM d, yyyy', { locale: dateFnsLocale }),
      tasks: groups[key],
    }));
  }, [displayedTasks, t]);

  const hasActiveFilters = searchQuery.trim() !== '' || startDate !== defaultStartDate || endDate !== defaultEndDate;

  return (
    <div className="flex flex-col h-full">
      <Header title={t('dashboard.ui.taskHistory')} />

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Compact Filter Bar */}
        <div className="mb-6">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            {/* Inline Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder={t('dashboard.ui.searchCompletedTasks')}
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                  }
                  debounceTimerRef.current = setTimeout(() => {
                    setPage(1);
                    loadHistoryWithFilters({
                      start: startDate,
                      end: endDate,
                      search: value,
                      pageNum: 1,
                    });
                  }, 300);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border transition-colors bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[36px]"
              />
            </div>

            {/* Filter Toggle */}
            <button
              type="button"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[36px] ${
                filtersOpen || hasActiveFilters
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
              }`}
              aria-expanded={filtersOpen}
              aria-label={t('dashboard.ui.toggleDateFilters')}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">{t('history.filters')}</span>
              {hasActiveFilters && !filtersOpen && (
                <span className="w-2 h-2 rounded-full bg-primary-500" />
              )}
            </button>

            {/* Clear button - only when filters active */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                aria-label={t('dashboard.ui.clearAllFilters')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>

          {/* Expandable Date Filters */}
          {filtersOpen && (
            <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm animate-in fade-in duration-200">
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="flex-1 w-full sm:w-auto">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('history.from')}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[36px]"
                  />
                </div>
                <div className="flex-1 w-full sm:w-auto">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('history.to')}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[36px]"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleApplyFilters}
                  className="w-full sm:w-auto"
                >
                  {t('history.apply')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Results count */}
        {hasSearched && !isLoading && total > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            {t('history.foundTasks', { count: total })}
          </p>
        )}

        {/* Task List */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader size="md" />
          </div>
        ) : displayedTasks.length > 0 ? (
          <div className="space-y-8">
            {groupedTasks.map(({ dateKey, label, tasks: groupTasks }) => (
              <div key={dateKey}>
                {/* Date Group Header */}
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {label}
                  </h2>
                  <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                  <span className="text-xs text-gray-400 dark:text-gray-600">
                    {t('history.taskCount', { count: groupTasks.length })}
                  </span>
                </div>

                {/* Task Cards in Group */}
                <div className="space-y-2">
                  {groupTasks.map((task: Task) => (
                    <HistoryTaskCard
                      key={task.id}
                      task={task}
                      priority={getPriorityColor(task.priority)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-4xl mb-4">📋</div>
            <p className="text-gray-700 dark:text-gray-200 font-medium mb-1">{t('history.noCompletedTasks')}</p>
            {hasSearched ? (
              <p className="text-sm text-gray-400">{t('history.tryAdjusting')}</p>
            ) : (
              <p className="text-sm text-gray-400">{t('history.willAppearHere')}</p>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8 pb-4">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors min-h-[36px] disabled:opacity-40 disabled:cursor-not-allowed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('history.previous')}
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
              {t('history.pageOf', { page, totalPages })}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors min-h-[36px] disabled:opacity-40 disabled:cursor-not-allowed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600"
            >
              {t('history.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
